'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import NextLink from 'next/link';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { BotyIcon } from '@boty/ui';
import { useLocale } from 'next-intl';
import { LanguageSwitcher } from './LanguageSwitcher';

export interface NavItem {
  label: string;
  href: string;
  /** If true, opens in a new tab (for external links) */
  external?: boolean;
  /** Optional icon element to display before the label */
  icon?: React.ReactNode;
}

export interface AppHeaderProps {
  /** Navigation items to display */
  navItems?: NavItem[];
  /** Optional right-side content (e.g., user menu for authenticated pages) */
  rightContent?: React.ReactNode;
  /** Whether to show the language switcher (default: true) */
  showLanguageSwitcher?: boolean;
}

/**
 * AppHeader - Shared header component for all pages
 *
 * Features:
 * - Boty logo that links to home
 * - Configurable navigation items
 * - Responsive: desktop links, mobile hamburger menu
 * - Optional right-side content slot for user menus, etc.
 */
export function AppHeader({
  navItems = [],
  rightContent,
  showLanguageSwitcher = true,
}: AppHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const locale = useLocale();
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavClick = (item: NavItem) => {
    handleMenuClose();
    if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      router.push(item.href);
    }
  };

  const handleLogoClick = () => {
    router.push(`/${locale}`);
  };

  const hasNavItems = navItems.length > 0;

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo */}
        <Box
          onClick={handleLogoClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <BotyIcon size={40} variant="full" />
        </Box>

        {/* Right side: language switcher, nav items, custom content */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Language Switcher */}
          {showLanguageSwitcher && <LanguageSwitcher variant="minimal" />}

          {/* Desktop Navigation */}
          {!isMobile && hasNavItems && (
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              {navItems.map((item, index) =>
                item.external ? (
                  <MuiLink
                    key={`${item.href}-${index}`}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </MuiLink>
                ) : (
                  <MuiLink
                    key={`${item.href}-${index}`}
                    component={NextLink}
                    href={item.href}
                    underline="none"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </MuiLink>
                )
              )}
            </Box>
          )}

          {/* Custom right content (e.g., user menu) - shown on both desktop and mobile */}
          {rightContent}

          {/* Mobile Hamburger Menu - only for nav items */}
          {isMobile && hasNavItems && (
            <>
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {navItems.map((item, index) => (
                  <MenuItem
                    key={`${item.href}-${index}`}
                    onClick={() => handleNavClick(item)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {item.icon}
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default AppHeader;
